import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { ConfirmDialog } from "../components/dialog.tsx";

/**
 * Unsaved-change protection (docs/02 shared states). Pages register `useUnsavedGuard(isDirty)`; in-app links
 * use `useGuardedNavigate`, browser back is intercepted, and closing/reloading the tab triggers beforeunload.
 */
interface GuardCtx {
  setDirty(id: string, dirty: boolean): void;
  navigate(to: string): void;
}
const Ctx = createContext<GuardCtx | null>(null);

// Module-level so the session layer can ask whether an expired session would discard work (UX-19).
const dirtyForms = new Set<string>();
export const hasUnsavedChanges = () => dirtyForms.size > 0;

export function GuardProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const dirty = useRef(dirtyForms);
  const [pending, setPending] = useState<string | null>(null);
  const bypass = useRef(false);
  const current = useRef(location);
  useEffect(() => {
    current.current = location;
  }, [location]);

  const isDirty = () => dirty.current.size > 0;

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) e.preventDefault();
    };
    // Browser back/forward: undo the history move and ask first.
    const onPop = () => {
      if (bypass.current || !isDirty()) return;
      const target = window.location.pathname + window.location.search;
      window.history.pushState(null, "", current.current);
      setPending(target);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  const value: GuardCtx = {
    setDirty(id, d) {
      if (d) dirty.current.add(id);
      else dirty.current.delete(id);
    },
    navigate(to) {
      // Same destination (double click, Radix tabs firing on focus and click): never stack duplicate entries.
      if (to === window.location.pathname + window.location.search) return;
      if (isDirty()) setPending(to);
      else setLocation(to);
    },
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Sair sem salvar?"
        confirmLabel="Sair sem salvar"
        danger
        onConfirm={() => {
          const to = pending!;
          dirty.current.clear();
          setPending(null);
          bypass.current = true;
          setLocation(to);
          setTimeout(() => (bypass.current = false), 0);
        }}
      >
        <p className="text-sm">Há alterações que ainda não foram salvas. Se sair agora, elas serão perdidas.</p>
      </ConfirmDialog>
    </Ctx.Provider>
  );
}

let seq = 0;
export function useUnsavedGuard(dirty: boolean) {
  const ctx = useContext(Ctx);
  const id = useRef(`g${++seq}`);
  useEffect(() => {
    ctx?.setDirty(id.current, dirty);
  }, [ctx, dirty]);
  useEffect(() => {
    const key = id.current;
    return () => ctx?.setDirty(key, false);
  }, [ctx]);
}

export function useGuardedNavigate() {
  const ctx = useContext(Ctx);
  const [, setLocation] = useLocation();
  return useCallback((to: string) => (ctx ? ctx.navigate(to) : setLocation(to)), [ctx, setLocation]);
}
